import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { formatEuro, formatDateISO } from '../lib/format.js';
import PageHeader from '../components/ui/PageHeader.jsx';
import StatusBadge from '../components/ui/StatusBadge.jsx';
import QuoteComposer from '../components/QuoteComposer.jsx';

export default function QuotesPage() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const qs = new URLSearchParams();
  if (search) qs.set('q', search);
  if (status) qs.set('status', status);
  qs.set('page', page);
  qs.set('pageSize', 20);

  const { data, loading, error, refresh } = useApi('/quotes?' + qs.toString(), { deps: [search, status, page] });

  const quotes = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  function openNew() { setEditingId(null); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  function openEdit(id) { setEditingId(id); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  function closeForm() { setShowForm(false); setEditingId(null); refresh(); }

  return (
    <>
      <PageHeader
        title="Preventivi"
        subtitle={data ? data.total + ' preventivi' : 'Generatore preventivi forfettari'}
        action={<button className="btn btn-primary" onClick={openNew}>+ Nuovo preventivo</button>}
      />
      <div className="card-elevated mb-4 flex flex-wrap items-center gap-3">
        <input className="input flex-1 min-w-[200px]" placeholder="Cerca numero, cliente, titolo..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <select className="input w-auto" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">Tutti gli stati</option>
          <option value="draft">Bozza</option>
          <option value="sent">Inviato</option>
          <option value="accepted">Accettato</option>
          <option value="rejected">Rifiutato</option>
          <option value="expired">Scaduto</option>
          <option value="invoiced">Fatturato</option>
        </select>
      </div>
      {error && <div className="p-3 rounded-md text-sm mb-4" style={{background:'rgba(239,68,68,.1)',color:'#ef4444'}}>{error}</div>}
      {showForm && (
        <div className="card-elevated mb-4">
          <QuoteComposer quoteId={editingId} onSaved={closeForm} onCancel={closeForm} />
        </div>
      )}
      <div className="card-elevated overflow-x-auto">
        <table className="table-root">
          <thead>
            <tr>
              <th className="table-header">N°</th>
              <th className="table-header">Titolo</th>
              <th className="table-header">Cliente</th>
              <th className="table-header">Data</th>
              <th className="table-header">Scadenza</th>
              <th className="table-header">Stato</th>
              <th className="table-header text-right">Totale</th>
              <th className="table-header"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="table-cell text-center py-8" colSpan={8}>Caricamento...</td></tr>}
            {!loading && quotes.length === 0 && <tr><td className="table-cell text-center py-8" colSpan={8}>Nessun preventivo <button className="btn btn-primary text-xs ml-3" onClick={openNew}>Crea primo</button></td></tr>}
            {!loading && quotes.map(q => (
              <tr key={q.id} className="table-row cursor-pointer transition-colors" onClick={() => navigate('/preventivi/' + q.id)}>
                <td className="table-cell font-mono text-brand-300">{q.number}</td>
                <td className="table-cell">{q.title}</td>
                <td className="table-cell">{q.display_name || q.legal_name || '\u2014'}</td>
                <td className="table-cell">{formatDateISO(q.issue_date)}</td>
                <td className="table-cell">{formatDateISO(q.validity_date)}</td>
                <td className="table-cell"><StatusBadge status={q.status} small /></td>
                <td className="table-cell text-right font-mono">{formatEuro(q.total_amount)}</td>
                <td className="table-cell text-right">
                  <button className="btn btn-ghost text-xs" onClick={(e) => { e.stopPropagation(); openEdit(q.id); }}>Modifica</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--color-line)' }}>
            <span className="text-xs text-surface-4">Pagina {page} di {totalPages}</span>
            <div className="flex gap-2">
              <button className="btn btn-secondary text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>\u2190 Prec</button>
              <button className="btn btn-secondary text-xs" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Succ \u2192</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
